/**
 * ReferralsTab — the front desk's Referrals list (PROD-1395), a Calendar tab behind `?x=referrals`.
 * It lists the referrals physicians send (Scrips.Patient `api/Patients/referrals`) and books an appointment
 * for one: Book opens the existing create-appointment panel through the handoff store, and when the
 * panel reports the new appointment id the referral is marked booked.
 *
 * Structure and shared pieces follow follow-up-requests-tab.tsx: the list-table card,
 * BridgeStatusChip, RowActions, ListPaginationFooter and the loading, empty and error states.
 * No role or permission check here: the server decides what the caller may see.
 */

import { useState } from 'react';
import { AlertCircle, CalendarPlus, CheckCircle2 } from 'lucide-react';
import { EmptyState, Segmented, Select } from '@scripsteam/scrips-signal-ds';
import type { SelectOption } from '@scripsteam/scrips-signal-ds';
import { toast } from 'sonner';
import type { OrgLocation } from '@/auth/auth-api';
import { BridgeStatusChip } from '@/components/ui/status-chip';
import { RowActions, type RowAction } from '@/components/blocks/row-actions';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ListPaginationFooter } from '@/shared/ui/molecules/list-pagination-footer';
import { formatDate } from '@/shared/lib/date-format';
import { formatMrnLabel } from '@/shared/lib/mrn';
import { parseWallClock } from '@/shared/lib/wall-clock';
import { useReferralsUnmarkedStore } from '@/scheduling/store/referrals-unmarked-store';
import { useCreateAppointmentHandoff } from '@/shared/store/create-appointment-handoff';
import { useMarkReferralBooked, useReferrals } from '@/scheduling/services/use-referrals';
import type { ReferralRow } from '@/types/referral-api';
import { buildReferralBookingPrefill } from './referral-booking';
import {
  ALL_ROLES,
  REFERRAL_DEFAULT_PAGE_SIZE,
  REFERRAL_PAGE_SIZES,
  REFERRAL_ROLES,
  REFERRAL_STATUS_FILTERS,
  canBook,
  providerFilterFor,
  roleLabel,
  statusesFor,
  targetPerson,
  type ReferralPageSize,
  type ReferralStatusFilter,
} from './referral-config';

const STATUS_OPTIONS = REFERRAL_STATUS_FILTERS.map((f) => ({ value: f.value, label: f.label }));
const ROLE_OPTIONS: SelectOption[] = [
  { value: ALL_ROLES, label: 'All roles' },
  ...REFERRAL_ROLES.map((r) => ({ value: r.value, label: r.label })),
];

/** The message when the appointment exists but the referral could not be marked booked. */
export const MARK_BOOKED_FAILED =
  'The appointment was booked, but the referral could not be marked booked. Use Mark booked to try again.';

/** The save worked but the server sent no appointment id, so the referral cannot be marked booked. */
export const BOOKED_WITHOUT_ID =
  'The appointment was booked, but its id did not come back. Check the calendar before booking again.';

/** Local YYYY-MM-DD (avoids the toISOString UTC shift near midnight). */
function todayLocalISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function ReferralsTab({ location = null }: { location?: OrgLocation | null }) {
  const [statusFilter, setStatusFilter] = useState<ReferralStatusFilter>('open');
  const [role, setRole] = useState<string>(ALL_ROLES);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<ReferralPageSize>(REFERRAL_DEFAULT_PAGE_SIZE);

  const { data, isLoading, isError, isFetching } = useReferrals({
    statuses: statusesFor(statusFilter),
    performerRole: role === ALL_ROLES ? undefined : role,
    page,
    pageSize,
  });
  const markBooked = useMarkReferralBooked();
  // referralId -> appointmentId, for referrals whose appointment exists but whose PATCH failed.
  // Those rows offer Mark booked (same id) and never Book, so no second appointment is made.
  // Kept in a store (sessionStorage), so it survives a tab switch and a reload.
  const unmarked = useReferralsUnmarkedStore((st) => st.unmarked);
  const remember = useReferralsUnmarkedStore((st) => st.remember);
  const forget = useReferralsUnmarkedStore((st) => st.forget);
  const [markingIds, setMarkingIds] = useState<ReadonlySet<string>>(new Set());

  const mark = (row: ReferralRow, appointmentId: string) => {
    setMarkingIds((prev) => new Set(prev).add(row.id));
    markBooked
      .mutateAsync({ patientId: row.patient.id, referralId: row.id, appointmentId })
      .then(() => forget(row.id))
      .catch(() => {
        remember(row.id, appointmentId);
        toast.error(MARK_BOOKED_FAILED);
      })
      .finally(() =>
        setMarkingIds((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        }),
      );
  };
  const requestCreateAppointment = useCreateAppointmentHandoff((s) => s.requestCreateAppointment);

  const changeStatus = (next: ReferralStatusFilter) => {
    setStatusFilter(next);
    setPage(1);
  };
  const changeRole = (next: string) => {
    setRole(next);
    setPage(1);
  };
  const changePageSize = (next: ReferralPageSize) => {
    setPageSize(next);
    setPage(1);
  };

  const book = (row: ReferralRow) => {
    // The location the Calendar is showing (an empty id is guarded in the builder, so the panel picks one).
    requestCreateAppointment(buildReferralBookingPrefill(row, location, todayLocalISO()), {
      providerFilter: providerFilterFor(row),
      // The appointment exists by now. A failure here never undoes it: the row simply stays open.
      onBooked: (appointmentId) => mark(row, appointmentId),
      onBookedWithoutId: () => toast.warning(BOOKED_WITHOUT_ID),
    });
  };

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-[var(--page-pad)] pt-[var(--page-header-gap)] pb-4">
        <div className="flex flex-wrap items-center gap-4">
          <Segmented<ReferralStatusFilter>
            aria-label="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={changeStatus}
          />
          <div className="w-56">
            <Select
              aria-label="Role"
              size="compact"
              options={ROLE_OPTIONS}
              value={role}
              onChange={changeRole}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto scrollbar-hide px-[var(--page-pad)] pb-[var(--page-pad)]">
        <section className="list-table w-fit min-w-full">
          <div className="list-table-title">
            <div className="flex items-baseline gap-2">
              <h2>Referrals</h2>
              {data ? <p className="text-sm font-normal text-text-secondary">{total} total</p> : null}
            </div>
          </div>
          {isLoading ? (
            <LoadingTable />
          ) : isError ? (
            <ErrorState />
          ) : rows.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon="file"
                title={statusFilter === 'open' ? 'No open referrals' : 'No referrals found'}
                description="Referrals that physicians send appear here."
              />
            </div>
          ) : (
            <ReferralsTable rows={rows} unmarked={unmarked} markingIds={markingIds} onBook={book} onMark={mark} />
          )}
        </section>
      </div>

      {!isLoading && !isError && data ? (
        <ListPaginationFooter
          pageNumber={data.page ?? page}
          pageSize={pageSize}
          pageSizes={REFERRAL_PAGE_SIZES}
          totalRecords={total}
          totalPages={totalPages}
          isFetching={isFetching}
          onPageChange={setPage}
          onPageSizeChange={changePageSize}
        />
      ) : null}
    </div>
  );
}

interface RowHandlers {
  unmarked: Record<string, string>;
  markingIds: ReadonlySet<string>;
  onBook: (row: ReferralRow) => void;
  onMark: (row: ReferralRow, appointmentId: string) => void;
}

function ReferralsTable({ rows, ...handlers }: { rows: ReferralRow[] } & RowHandlers) {
  return (
    <table className="w-full">
      <thead className="sticky top-0 z-10">
        <tr>
          <th className="text-left">Patient</th>
          <th className="text-left">Referred to</th>
          <th className="text-left">Reason</th>
          <th className="text-left">Requested by</th>
          <th className="text-left">Requested</th>
          <th className="text-left">Status</th>
          <th aria-label="Actions" />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <ReferralRowView key={row.id} row={row} {...handlers} />
        ))}
      </tbody>
    </table>
  );
}

function ReferralRowView({ row, unmarked, markingIds, onBook, onMark }: { row: ReferralRow } & RowHandlers) {
  const mrn = formatMrnLabel(row.patient.mrn);
  const person = targetPerson(row);
  const created = formatDate(parseWallClock(row.createdDate));
  const savedAppointmentId = unmarked[row.id];
  const marking = markingIds.has(row.id);
  const actions: RowAction[] = !canBook(row.status)
    ? []
    : savedAppointmentId
      ? [
          {
            label: 'Mark booked',
            icon: <CheckCircle2 className="size-4" aria-hidden />,
            onClick: () => onMark(row, savedAppointmentId),
            disabled: marking,
          },
        ]
      : [
          {
            label: 'Book appointment',
            icon: <CalendarPlus className="size-4" aria-hidden />,
            onClick: () => onBook(row),
            disabled: marking,
          },
        ];
  return (
    <tr className="hover:bg-grey-100/60">
      <Td>
        <div className="font-semibold text-text-primary">{row.patient.name?.trim() || '—'}</div>
        {mrn ? <div className="text-sm text-text-muted">{mrn}</div> : null}
      </Td>
      <Td>
        <div>{roleLabel(row.performerRole)}</div>
        {person ? <div className="text-sm text-text-muted">{person}</div> : null}
      </Td>
      <Td className="truncate max-w-72" title={row.reasonDisplay ?? undefined}>
        {row.reasonDisplay?.trim() || '—'}
      </Td>
      <Td>{row.referrerName?.trim() || '—'}</Td>
      <Td className="tabular-nums">{created || '—'}</Td>
      <Td>
        <ReferralStatusChip status={row.status} />
      </Td>
      <td className="px-2 py-2 text-right">
        <RowActions actions={actions} label={`referral for ${row.patient.name ?? 'patient'}`} />
      </td>
    </tr>
  );
}

/** Same look as follow-up-requests-tab renderStatus: no blue chips (blue is for links, the active tab and the one primary button). */
export function ReferralStatusChip({ status }: { status: string | null | undefined }) {
  const raw = (status ?? '').trim();
  const c = raw.toLowerCase();
  const label = c.charAt(0).toUpperCase() + c.slice(1);
  const semantic =
    c === 'sent' || c === 'accepted' ? 'warning'
    : c === 'booked' || c === 'completed' ? 'success'
    : c === 'cancelled' ? 'neutral'
    : null;
  if (!raw) return <BridgeStatusChip semantic="neutral">—</BridgeStatusChip>;
  if (semantic) return <BridgeStatusChip semantic={semantic}>{label}</BridgeStatusChip>;
  return (
    <BridgeStatusChip semantic="neutral" title={`Unmapped status: ${raw}`}>
      {raw}
    </BridgeStatusChip>
  );
}

function Td({ children, className, title }: { children: React.ReactNode; className?: string; title?: string }) {
  return (
    <td title={title} className={cn('text-text-primary align-middle', className)}>
      {children}
    </td>
  );
}

function LoadingTable() {
  return (
    <div className="p-6 space-y-3" aria-busy="true">
      <Skeleton className="h-5 w-1/3" />
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function ErrorState() {
  return (
    <div className="p-12 text-center" role="alert">
      <AlertCircle className="size-8 text-status-error mx-auto" aria-hidden />
      <p className="mt-2 text-sm font-semibold text-status-error">Couldn't load referrals</p>
      <p className="mt-1 text-sm text-text-muted">Try refreshing the page. If the problem stays, contact support.</p>
    </div>
  );
}
